import { motion } from "framer-motion";
import { useEffect } from "react";
import { useScene } from "./scene/SceneController";

const problems = [
  {
    title: "Documents Everywhere",
    description: "Invoices, receipts, statement and client files scattered across email, WhatsApp, drives and folders.",
    icon: "📄",
  },
  {
    title: "Experts Doing Repetition",
    description: "Qualified accountants spend hours downloading, renaming, checking and moving files.",
    icon: "⏳",
  },
  {
    title: "No Real-Time Visibility",
    description: "Partners discover problems late because information is trapped in disconnected systems.",
    icon: "📊",
  },
];

const ProblemSection = () => {
  const { updateScene } = useScene();

  useEffect(() => {
    updateScene({
      section: "problem",

      chaos: 1,

      organization: 0,

      coreIntensity: 1.3,

      cameraDistance: 10,
    });
  }, [updateScene]);

  return (
    <section
      id="problem"
      className="tw-relative tw-min-h-screen tw-pt-28 tw-pb-16 tw-overflow-hidden"
    >
      {/* Background */}
      <div
        className="
          tw-absolute
          tw-inset-0
          tw-bg-gradient-to-b
          tw-from-[#050816]
          tw-via-[#111827]
          tw-to-[#050816]
        "
      />

      <div
        className="
          tw-relative
          tw-z-10
          tw-max-w-6xl
          tw-mx-auto
        "
      >
        {/* Heading */}

        <motion.div
          initial={{
            opacity: 0,
            y: 40,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
          }}
          className="
            tw-text-center
            tw-max-w-3xl
            tw-mx-auto
          "
        >
          <p
            className="
              tw-text-red-400
              tw-uppercase
              tw-tracking-[0.25em]
              tw-text-sm
              tw-mb-5
            "
          >
            The hidden problem
          </p>

          <h2
            className="
              tw-text-4xl
              md:tw-text-6xl
              tw-font-bold
              tw-text-white
            "
          >
            Your team's talent is
            <br />
            buried under
            <span className="tw-text-red-400">
              {" "}manual work.
            </span>
          </h2>

          <p
            className="
              tw-mt-6
              tw-text-lg
              tw-text-gray-300
              tw-leading-relaxed
            "
          >
            Modern CA firms don't struggle because accountants lack knowledge.
            They struggle because their best people spend too much time managing
            information instead of using it.
          </p>
        </motion.div>

        {/* Problem Cards */}

        <div
          className="
            tw-mt-20
            tw-grid
            md:tw-grid-cols-3
            tw-gap-8
          "
        >
          {problems.map((item, index) => (
            <motion.div
              key={item.title}

              initial={{
                opacity: 0,
                y: 50,
              }}

              whileInView={{
                opacity: 1,
                y: 0,
              }}

              viewport={{
                once: true,
              }}

              transition={{
                delay: index * 0.15,
                duration: 0.6,
              }}

              whileHover={{
                y: -10,
                scale: 1.03,
                transition: {
                  duration: 0.15,
                  ease: "easeOut",
                },
              }}

              className="
                tw-group
                tw-rounded-3xl
                tw-p-8
                tw-bg-white/5
                tw-backdrop-blur-xl
                tw-border
                tw-border-white/10
                hover:tw-border-red-400/50
                hover:tw-bg-white/10
                tw-cursor-pointer
                tw-transition-colors
                tw-duration-150
              "
            >
              <div
                className="
                  tw-text-4xl
                  tw-mb-6
                "
              >
                {item.icon}
              </div>

              <h3
                className="
                  tw-text-xl
                  tw-font-semibold
                  tw-text-white
                  tw-mb-3
                "
              >
                {item.title}
              </h3>

              <p
                className="
                  tw-text-gray-400
                  tw-leading-relaxed
                "
              >
                {item.description}
              </p>

              {/* Bottom warning line */}

              <motion.div
                initial={{
                  width: "0%",
                }}
                whileInView={{
                  width: "100%",
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  delay: index * 0.2 + 0.5,
                  duration: 1,
                }}
                className="
                  tw-mt-8
                  tw-h-[2px]
                  tw-bg-gradient-to-r
                  tw-from-red-500
                  tw-to-orange-400
                "
              />
            </motion.div>
          ))}
        </div>

        {/* Transition statement */}

        <motion.div
          initial={{
            opacity: 0,
            scale: 0.95,
          }}
          whileInView={{
            opacity: 1,
            scale: 1,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.8,
          }}
          className="
            tw-mt-20
            tw-mx-auto
            tw-max-w-3xl
            tw-text-center
            tw-p-8
            tw-rounded-3xl
            tw-bg-red-500/10
            tw-border
            tw-border-red-500/20
          "
        >
          <h3 className="tw-text-2xl tw-font-semibold tw-text-white">
            The goal isn't to replace accountants.
          </h3>

          <p
            className="
              tw-mt-3
              tw-text-gray-300
            "
          >
            The goal is to remove the work that prevents accountants from doing what they do best: applying judgement, advising clients and growing the firm.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;